// English strings. Other languages must provide exactly the same keys
// (enforced by the `Translation` type).

const en = {
  app: {
    name: 'Dishly',
  },
  common: {
    cancel: 'Cancel',
    close: 'Close',
    save: 'Save',
    loading: 'Loading…',
    retry: 'Try again',
    create: 'Create',
    undo: 'Undo',
    more: 'More options',
  },
  nav: {
    home: 'Dishly home',
    add: 'Add',
    addRecipe: 'Add a recipe',
    shoppingList: 'Shopping list',
    lists: 'My lists',
    myRecipes: 'My recipes',
    account: 'Account',
    logIn: 'Log in / Sign up',
    logOut: 'Log out',
    themeDark: 'Switch to dark theme',
    themeLight: 'Switch to light theme',
    language: 'Language',
    userMenu: 'Account menu',
    offline: 'Offline',
    offlineHint: "You're offline. Downloaded recipes and your lists still work.",
  },
  home: {
    title: 'Recipes',
    count_one: '{{count}} recipe',
    count_other: '{{count}} recipes',
    tags: 'Tags',
    showAll: 'Show all',
    sortLabel: 'Sort by',
    sort: {
      newest: 'Newest',
      rating: 'Top rated',
      quickest: 'Quickest',
      name: 'A–Z',
    },
    viewGrid: 'Grid view',
    viewList: 'List view',
    empty: 'No recipes yet.',
    emptyHint: 'Be the first to share one!',
    noMatches: 'No recipes match all of these tags.',
    noMatchesHint: 'Try removing a tag.',
    offlineEmpty: "You're offline and no recipes have been downloaded yet. Connect once and they'll be available offline.",
    end: "That's everything!",
    backToTop: 'Back to top',
  },
  tags: {
    breakfast: 'Breakfast',
    brunch: 'Brunch',
    lunch: 'Lunch',
    dinner: 'Dinner',
    vegetarian: 'Vegetarian',
    vegan: 'Vegan',
    'gluten-free': 'Gluten free',
  },
  rating: {
    none: 'No ratings yet',
    label_one: 'Rated {{rating}} out of 5 by {{count}} person',
    label_other: 'Rated {{rating}} out of 5 by {{count}} people',
    rateStars_one: 'Rate {{count}} star',
    rateStars_other: 'Rate {{count}} stars',
  },
  cart: {
    add: 'Add to shopping list',
    remove: 'Remove from shopping list',
    added: 'Added to your shopping list',
    removed: 'Removed from your shopping list',
    view: 'View',
  },
  badge: {
    draft: 'Draft',
    private: 'Private',
  },
  recipe: {
    by: 'by {{name}}',
    servings_one: '{{count}} serving',
    servings_other: '{{count}} servings',
    fewerServings: 'Fewer servings',
    moreServings: 'More servings',
    units: 'Units',
    metric: 'Metric',
    imperial: 'Imperial',
    ingredients: 'Ingredients',
    noIngredients: 'No ingredients listed.',
    method: 'Method',
    startCooking: 'Start cooking',
    continueCooking: 'Continue from step {{step}}',
    stepOf: 'Step {{current}} of {{total}}',
    next: 'Next step',
    finish: 'Finish',
    returnToCurrent: 'Return to current step',
    progress: 'Progress through the recipe',
    goToStep: 'Go to step {{step}}',
    stepDone: 'Step done',
    startOver: 'Start over',
    finishedTitle: 'Enjoy your meal!',
    finishedBody: 'How did it turn out?',
    rateThis: 'Rate this recipe',
    yourRating: 'Your rating',
    ownRecipe: 'This is your recipe, so you can’t rate it.',
    reviews: 'Reviews',
    noReviews: 'No comments yet.',
    loadMore: 'Show more',
    favourite: 'Add to favourites',
    unfavourite: 'Remove from favourites',
    favourited: 'Added to favourites',
    unfavourited: 'Removed from favourites',
    saveToList: 'Save to list',
    newListPlaceholder: 'New list name',
    edit: 'Edit',
    delete: 'Delete',
    deleteTitle: 'Delete this recipe?',
    deleteBody: 'It will be removed for everyone. This can’t be undone.',
    deleted: 'Recipe deleted',
    draftNotice: 'This is a draft. Only you can see it.',
    privateNotice: 'This recipe is private. Only you can see it.',
    notFound: 'Recipe not found',
    notFoundBody: 'It may have been deleted or made private.',
    notDownloaded: 'This recipe hasn’t been downloaded yet. Connect to the internet to open it. After that, it works offline.',
    addThese: 'Add the following:',
    notes: 'Notes',
    photo: 'Photo {{n}} of {{total}}',
    startTimer: 'Start {{duration}} timer',
    timerRunning: 'Timer running',
  },
  steps: {
    add_ingredients: 'Add ingredients',
    cook: 'Cook',
    prep: 'Prep',
  },
  heat: {
    low: 'Low heat',
    medium_low: 'Medium-low heat',
    medium: 'Medium heat',
    medium_high: 'Medium-high heat',
    high: 'High heat',
  },
  method: {
    pan: 'Frying pan',
    pot: 'Saucepan',
    oven: 'Oven',
    grill: 'Grill',
    bbq: 'Barbecue',
    steamer: 'Steamer',
    deep_fryer: 'Deep fryer',
    air_fryer: 'Air fryer',
    microwave: 'Microwave',
    slow_cooker: 'Slow cooker',
    pressure_cooker: 'Pressure cooker',
    other: 'Other',
  },
  review: {
    title: 'Thanks for rating!',
    saved: 'Rating saved',
    commentLabel: 'Add a comment (optional)',
    commentPlaceholder: 'What did you think? Any tweaks?',
    post: 'Post comment',
    skip: 'No thanks',
    editComment: 'Edit comment',
    remove: 'Remove my rating',
    removed: 'Rating removed',
  },
  lists: {
    title: 'My lists',
    shopping: 'Shopping list',
    favourites: 'Favourites',
    new: 'New list',
    namePlaceholder: 'e.g. Weeknight dinners',
    rename: 'Rename list',
    delete: 'Delete list',
    deleteTitle: 'Delete “{{name}}”?',
    deleteBody: 'The recipes themselves aren’t deleted.',
    empty: 'This list is empty.',
    emptyShopping: 'Tap the basket on any recipe to add its ingredients here.',
    emptyOther: 'Save recipes to this list from any recipe page.',
    browse: 'Browse recipes',
    recipes_one: '{{count}} recipe',
    recipes_other: '{{count}} recipes',
    consolidate: 'Combine ingredients',
    consolidateHint: 'One list for everything. Collapsed recipes are left out.',
    collapsedExcluded_one: '{{count}} collapsed recipe isn’t included.',
    collapsedExcluded_other: '{{count}} collapsed recipes aren’t included.',
    nothingToCombine: 'Expand a recipe to include its ingredients.',
    expand: 'Show ingredients',
    collapse: 'Hide ingredients (save for later)',
    remove: 'Remove from list',
    removed: 'Removed “{{name}}”',
    export: 'Export',
    copy: 'Copy to clipboard',
    copied: 'Copied! Paste it into Notes or any app.',
    copyFailed: 'Couldn’t copy the list.',
    share: 'Share…',
    unavailable: 'This recipe is no longer available.',
    uncheckAll: 'Untick all',
    progress: '{{done}} of {{total}} ticked',
  },
  myRecipes: {
    title: 'My recipes',
    drafts: 'Drafts',
    private: 'Private',
    public: 'Public',
    emptyDrafts: 'No drafts. Recipes you start are saved here automatically.',
    emptyPrivate: 'No private recipes.',
    emptyPublic: 'You haven’t published any recipes yet.',
    untitled: 'Untitled recipe',
    edited: 'Edited {{date}}',
  },
  newRecipe: {
    title: 'Add a recipe',
    subtitle: 'How would you like to start?',
    photoTitle: 'Snap a paper recipe',
    photoBody: 'Take a photo of a recipe card or cookbook page and AI turns it into a Dishly recipe.',
    describeTitle: 'Describe or say it',
    describeBody: 'Type or say what you have, like “chicken, potatoes, cauliflower and normal kitchen spices”, and AI creates a recipe.',
    manualTitle: 'Write it yourself',
    manualBody: 'Add your recipe step by step. Your work is saved as you go.',
    comingSoon: 'Coming soon',
    comingSoonBody: 'AI recipe creation isn’t available yet. You can write the recipe yourself in the meantime.',
    choosePhoto: 'Take or choose a photo',
    describePlaceholder: 'I have chicken, potatoes, cauliflower and normal kitchen spices…',
    generate: 'Create recipe',
    writeManually: 'Write it myself',
    drafts: 'Continue a draft',
    addPage: 'Add another page',
    photoHint: 'Up to {{count}} photos, such as both pages of a cookbook.',
    noteLabel: 'Anything to add? (optional)',
    notePlaceholder: 'e.g. “Make it for 6” or “Use the oven, not the grill”',
    voiceHint: 'Or tap the microphone and say it.',
    working: 'Writing your recipe…',
    workingPhotos: 'Reading your photos…',
    linkTitle: 'Paste a link',
    linkBody: 'Found a recipe on a website? Paste the link and AI brings it into Dishly, photo and all.',
    linkLabel: 'Link to the recipe',
    linkHint: 'Works with most recipe websites. The site is credited in the recipe, and you can check everything before publishing.',
    workingLink: 'Reading the page…',
    workingHint: 'This can take up to a minute. Please keep this page open.',
  },
  save: {
    button: 'Save to Dishly',
    title: 'Save to Dishly',
    dragHint: 'Drag this button to your bookmarks bar, then click it on any recipe page.',
    tip: 'Save recipes straight from your browser: drag this button to your bookmarks bar, then click it on a recipe page.',
    setUp: 'Drag this button to your bookmarks bar. Then, on any recipe page, click it to bring the recipe into Dishly.',
    howTo: 'This page opens when you click Save to Dishly in your bookmarks bar on a recipe page. On a phone, paste the recipe’s link or take screenshots instead.',
    popupBlocked: 'Allow pop-ups on this site to save the recipe to Dishly.',
    waiting: 'Getting the recipe page…',
    nothing: 'Nothing arrived from the recipe page',
    nothingHint: 'Go back to the recipe page and click Save to Dishly again.',
    confirm: 'Bring this recipe from {{site}} into Dishly? AI writes it as a draft for you to check.',
    import: 'Import recipe',
    blocked: '{{site}} doesn’t let apps read its recipes.',
    blockedDesktop: 'You can still save it from your browser: drag the button below to your bookmarks bar, open the recipe and click the bookmark. Or take screenshots and use “Snap a paper recipe”.',
    blockedMobile: 'Take screenshots of the recipe (up to 4) and use “Snap a paper recipe” instead.',
  },
  voice: {
    start: 'Record your voice',
    stop: 'Stop recording',
    say: 'Say it',
    transcribing: 'Transcribing…',
    denied: 'Microphone access is blocked. Allow it in your browser settings to use your voice.',
    unavailable: 'The microphone couldn’t be started.',
    nothingHeard: 'We didn’t catch that. Please try again.',
  },
  adapt: {
    open: 'Ask or change',
    title: 'Ask or change this recipe',
    intro: 'Ask a question, like “My oven has no broil setting, what can I use?”, or say what to change, like “I don’t have fish sauce”. Changes are just for you.',
    placeholder: 'Ask a question or say what to change…',
    submit: 'Send',
    working: 'Thinking…',
    you: 'You',
    changed: 'Recipe updated',
    noChange: 'Nothing was changed.',
    noticeTitle: 'Changed for you',
    noticeBody: 'Only you see this version. The original recipe stays as it is.',
    changedStep: 'Changed',
    saveCopy: 'Save as my copy',
    copySaved: 'Saved to your recipes',
    adaptedFrom: 'Adapted from [{{name}}]({{path}}) by {{author}}.',
    apply: 'Apply to recipe',
    applyTitle: 'Update your recipe with these changes?',
    applyBody: 'Everyone who can see this recipe will see the changed version.',
    applied: 'Recipe updated',
    applyInvalid: 'The changed recipe is missing something it needs. Save it as a copy, or edit the recipe instead.',
    discard: 'Back to original',
  },
  editor: {
    newTitle: 'New recipe',
    editTitle: 'Edit recipe',
    name: 'Recipe name',
    namePlaceholder: 'e.g. Grandma’s apple crumble',
    photos: 'Photos',
    photosHint: 'The first photo is the cover.',
    addPhotos: 'Add photos',
    uploading: 'Uploading…',
    cover: 'Cover',
    makeCover: 'Use as cover',
    removePhoto: 'Remove photo',
    uploadFailed: 'Upload failed: {{message}}',
    tooManyPhotos: 'You can add up to {{count}} photos.',
    description: 'About this recipe',
    descriptionPlaceholder: 'The story behind it, tips, what to serve it with…',
    write: 'Write',
    preview: 'Preview',
    markdownHint: 'Markdown supported: **bold**, *italic*, lists and links.',
    nothingToPreview: 'Nothing to preview yet.',
    details: 'Details',
    totalTime: 'Total time (minutes)',
    stepTotal: 'Timed steps add up to {{duration}}.',
    useStepTotal: 'Use this',
    servings: 'Servings',
    tags: 'Tags',
    steps: 'Steps',
    stepsHint: 'Readers see one step at a time while they cook.',
    noSteps: 'No steps yet. Add the first one below.',
    addStep: 'Add a step',
    stepTypeHelp: {
      add_ingredients: 'One or more ingredients with amounts',
      cook: 'Apply heat: sauté, bake, simmer…',
      prep: 'Chop, mix, rest, line a tray…',
    },
    stepNumber: 'Step {{n}}',
    stepType: 'Step type',
    instruction: 'Instruction',
    instructionOptional: 'Instruction (optional)',
    instructionPlaceholder: {
      add_ingredients: 'e.g. Whisk together in a large bowl',
      cook: 'e.g. Sauté until soft and golden',
      prep: 'e.g. Dice the tomatoes',
    },
    ingredients: 'Ingredients',
    addIngredient: 'Add ingredient',
    ingredient: 'Ingredient',
    ingredientPlaceholder: 'e.g. olive oil',
    quantity: 'Amount',
    quantityPlaceholder: 'e.g. 1½',
    unit: 'Unit',
    unitNone: 'whole / no unit',
    note: 'Note',
    notePlaceholder: 'e.g. finely chopped',
    removeIngredient: 'Remove ingredient',
    equipment: 'Equipment',
    heat: 'Heat',
    notSpecified: 'Not specified',
    temperature: 'Temperature (°{{unit}})',
    duration: 'Time (minutes)',
    durationHint: 'Adds a timer button to this step.',
    notes: 'Notes',
    addNotes: 'Add notes',
    notesPlaceholder: 'Tips or extra detail for this step',
    stepPhotos: 'Step photos',
    addStepPhotos: 'Add photos',
    moveUp: 'Move step up',
    moveDown: 'Move step down',
    deleteStep: 'Delete step',
    dragStep: 'Drag to reorder',
    overview: 'Ingredients overview',
    overviewHint: 'Built automatically from your “Add ingredients” steps.',
    overviewEmpty: 'Ingredients you add in steps appear here.',
    save: 'Save recipe',
    saveTitle: 'Save your recipe',
    saveBody: 'Who should be able to see it?',
    public: 'Public',
    publicHint: 'Share it with everyone on Dishly.',
    private: 'Private',
    privateHint: 'Only you can see it. You can publish it later.',
    recommended: 'Recommended',
    published: 'Recipe published!',
    savedPrivate: 'Saved to your private recipes.',
    status: {
      saving: 'Saving…',
      saved: 'Draft saved',
      local: 'Saved on this device',
      offline: 'Offline: saved on this device',
      error: 'Couldn’t save',
    },
    fixErrors: 'Please fix the highlighted fields.',
    errors: {
      required: 'Required',
      number: 'Enter a number',
      name: 'Give your recipe a name',
      steps: 'Add at least one step',
      instruction: 'Describe this step',
      ingredients: 'Add at least one ingredient',
      ingredientName: 'Enter the ingredient',
    },
    deleteDraft: 'Delete draft',
    deleteDraftTitle: 'Delete this draft?',
    deleteDraftBody: 'This can’t be undone.',
    restoreTitle: 'Unsaved changes found',
    restoreBody: 'You have changes to this recipe from {{date}} that weren’t saved.',
    aiTitle: 'Written by AI',
    aiBody: 'Check the ingredients, amounts and steps, then save. Nothing is published until you do.',
    restore: 'Restore them',
    discard: 'Discard',
    offlineUnavailable: 'This recipe isn’t downloaded. Connect to the internet to edit it.',
    notYours: 'You can only edit your own recipes.',
  },
  auth: {
    title: 'Welcome to Dishly',
    loginTab: 'Log in',
    signupTab: 'Sign up',
    email: 'Email',
    password: 'Password',
    displayName: 'Display name',
    displayNameHint: 'Shown on your recipes and reviews.',
    passwordHint: 'At least 8 characters.',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    forgot: 'Forgot your password?',
    logIn: 'Log in',
    createAccount: 'Create account',
    loginToContinue: 'Log in or create an account to continue.',
    welcomeBack: 'Welcome back, {{name}}!',
    checkEmailTitle: 'Check your email',
    checkEmailBody: 'We sent a link to {{email}}. Click it to confirm your address, then log in.',
    resend: 'Resend the email',
    resent: 'Sent! Check your inbox (and spam folder).',
    backToLogin: 'Back to log in',
    notVerified: 'Please confirm your email first. Check your inbox for the link.',
    recaptcha: 'Protected by reCAPTCHA: the Google <privacy>Privacy Policy</privacy> and <terms>Terms of Service</terms> apply.',
    verifyTitle: 'Confirm your email',
    verifying: 'Confirming your email…',
    verified: 'Email confirmed!',
    verifiedBody: 'You can now log in to Dishly.',
    verifyFailed: 'We couldn’t confirm your email.',
    verifyExpired: 'This link has expired. Enter your email to get a new one.',
    forgotTitle: 'Reset your password',
    forgotBody: 'Enter your email and we’ll send you a link to choose a new password.',
    sendLink: 'Send reset link',
    linkSent: 'If there’s an account for {{email}}, a reset link is on its way.',
    resetTitle: 'Choose a new password',
    newPassword: 'New password',
    confirmPassword: 'Confirm new password',
    passwordsDontMatch: 'The passwords don’t match.',
    savePassword: 'Save password',
    passwordUpdated: 'Password updated. You can now log in.',
    resetInvalid: 'This reset link is invalid or has expired.',
    requestNew: 'Request a new link',
  },
  account: {
    title: 'Account',
    profile: 'Profile',
    displayName: 'Display name',
    email: 'Email',
    memberSince: 'Member since {{date}}',
    saved: 'Saved',
    preferences: 'Preferences',
    units: 'Preferred units',
    metric: 'Metric (g, ml, °C)',
    imperial: 'Imperial (oz, cups, °F)',
    language: 'Default language',
    theme: 'Theme',
    themeSystem: 'System',
    themeLight: 'Light',
    themeDark: 'Dark',
    security: 'Password',
    resetPassword: 'Reset password',
    resetHint: 'We’ll email you a link to choose a new password.',
    resetSent: 'Check your email for the reset link.',
    logOut: 'Log out',
  },
  timers: {
    title: 'Timers',
    done: 'Done!',
    pause: 'Pause timer',
    resume: 'Resume timer',
    cancel: 'Cancel timer',
    dismiss: 'Dismiss',
    started: 'Timer started: {{duration}}',
    notificationTitle: 'Timer finished',
    goToStep: 'Go to step',
  },
  pwa: {
    updateAvailable: 'A new version of Dishly is available.',
    update: 'Update',
  },
  notFound: {
    title: 'Page not found',
    body: 'We couldn’t find what you were looking for.',
    home: 'Go to recipes',
  },
  errors: {
    generic: 'Something went wrong. Please try again.',
    network: 'You’re offline or the server can’t be reached.',
    codes: {
      invalid_credentials: 'That email and password combination isn’t right.',
      email_not_verified: 'Please confirm your email address first.',
      rate_limited: 'Too many attempts. Please wait a few minutes and try again.',
      captcha_required: 'Please complete the captcha check.',
      captcha_failed: 'The captcha check failed. Please try again.',
      captcha_unavailable: 'We couldn’t check the captcha. Please try again.',
      token_expired: 'This link has expired.',
      token_invalid: 'This link isn’t valid or has already been used.',
      email_failed: 'We couldn’t send the email. Please try again in a moment.',
      too_large: 'That’s too large.',
      unsupported_image: 'Please upload a JPEG, PNG, WebP, GIF or AVIF image.',
      invalid_image: 'That image couldn’t be processed.',
      too_many_lists: 'You’ve reached the maximum number of lists.',
      own_recipe: 'You can’t rate your own recipe.',
      not_found: 'Not found.',
      forbidden: 'You don’t have permission to do that.',
      unauthorized: 'Please log in to continue.',
      validation_error: 'Some fields are invalid.',
      invalid_recipe: 'Please fix the highlighted fields.',
      builtin_list: 'Built-in lists can’t be changed.',
      server_error: 'Something went wrong on our side.',
      ai_disabled: 'AI features aren’t available right now.',
      ai_unavailable: 'The AI service isn’t responding. Please try again in a moment.',
      ai_failed: 'The AI couldn’t finish. Please try again.',
      ai_refused: 'The AI can’t help with that request.',
      unsupported_audio: 'That recording format isn’t supported.',
      invalid_url: 'That doesn’t look like a web address.',
      page_unreachable: 'We couldn’t open that page. Check the link and try again.',
      page_blocked: 'That website doesn’t let apps read its recipes. Take a screenshot and use “Snap a paper recipe” instead.',
      not_a_page: 'That link isn’t a web page.',
    },
  },
  time: {
    seconds_one: '{{count}} sec',
    seconds_other: '{{count}} sec',
    minutes_one: '{{count}} min',
    minutes_other: '{{count}} min',
    hours_one: '{{count}} hr',
    hours_other: '{{count}} hr',
    hoursMinutes: '{{hours}} hr {{minutes}} min',
  },
  units: {
    g: 'g',
    kg: 'kg',
    oz: 'oz',
    lb: 'lb',
    ml: 'ml',
    l: 'l',
    tsp: 'tsp',
    tbsp: 'tbsp',
    cup_one: 'cup',
    cup_other: 'cups',
    fl_oz: 'fl oz',
    pint_one: 'pint',
    pint_other: 'pints',
    pinch_one: 'pinch',
    pinch_other: 'pinches',
    clove_one: 'clove',
    clove_other: 'cloves',
    slice_one: 'slice',
    slice_other: 'slices',
    can_one: 'can',
    can_other: 'cans',
    bunch_one: 'bunch',
    bunch_other: 'bunches',
    sprig_one: 'sprig',
    sprig_other: 'sprigs',
    handful_one: 'handful',
    handful_other: 'handfuls',
    package_one: 'pack',
    package_other: 'packs',
  },
  unitNames: {
    g: 'grams (g)',
    kg: 'kilograms (kg)',
    oz: 'ounces (oz)',
    lb: 'pounds (lb)',
    ml: 'millilitres (ml)',
    l: 'litres (l)',
    tsp: 'teaspoons (tsp)',
    tbsp: 'tablespoons (tbsp)',
    cup: 'cups',
    fl_oz: 'fluid ounces (fl oz)',
    pint: 'pints',
    pinch: 'pinch',
    clove: 'cloves',
    slice: 'slices',
    can: 'cans / tins',
    bunch: 'bunches',
    sprig: 'sprigs',
    handful: 'handfuls',
    package: 'packs',
  },
  unitGroups: {
    mass: 'Weight',
    volume: 'Volume',
    other: 'Other',
  },
};

export type Translation = typeof en;
export default en;
